import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2, Plus } from 'lucide-react'
import { BottomSheet } from '@/shared/components/BottomSheet'
import { AddEssentialForm } from './AddEssentialForm'
import type { Ingredient } from './types'

interface HouseholdEssentialsSectionProps {
  items: Ingredient[]
  onRemove: (id: string) => void
  isPending: boolean
  canManage: boolean
}

export function HouseholdEssentialsSection({
  items,
  onRemove,
  isPending,
  canManage,
}: HouseholdEssentialsSectionProps) {
  const { t } = useTranslation('pantry')
  const [formOpen, setFormOpen] = useState(false)

  return (
    <section aria-label={t('essentials.sectionTitle')} className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-muted uppercase tracking-wide">
          {t('essentials.sectionTitle')}
        </h2>
        {canManage && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="flex items-center gap-1 text-sm text-accent font-medium min-h-[44px] min-w-[44px] justify-center"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('essentials.addButton')}
          </button>
        )}
      </div>

      {items.length === 0 && (
        <p className="text-sm text-muted">{t('essentials.empty')}</p>
      )}

      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-3 py-1">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">{item.name}</p>
              <p className="text-xs text-muted">{item.canonicalUnit}</p>
            </div>
            {canManage && (
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                disabled={isPending}
                aria-label={t('essentials.deleteAriaLabel', { name: item.name })}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] text-danger hover:bg-red-50 rounded-lg disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      <BottomSheet open={formOpen} onClose={() => setFormOpen(false)} title={t('essentials.addTitle')}>
        <AddEssentialForm onSuccess={() => setFormOpen(false)} />
      </BottomSheet>
    </section>
  )
}
